import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="抱歉，您访问的文章不存在" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    about: {
      '@type': 'Thing',
      name: article.categoryName,
    },
    keywords: article.tags?.join(', '),
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`悠宝三岁,${article.tags?.join(',')},${article.categoryName}`}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '成长方案', path: '/category' },
                { label: article.categoryName, path: `/category?tab=${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-user-line"></i>
                <span>{article.author}</span>
              </div>
            </div>

            <div className="w-full rounded-2xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full object-cover aspect-[16/9]"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags?.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-4">相关文章</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.slug}`}
                        className="flex gap-4 p-4 rounded-xl border border-background-200/70 hover:border-background-300 transition-all cursor-pointer"
                      >
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                        />
                        <div>
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-1">
                            {ra.title}
                          </h4>
                          <span className="text-xs text-foreground-500">{ra.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}